import { createContext, useContext, useMemo } from "react";

const AppModeContext = createContext({ isAppMode: false });

const STORAGE_KEY = "subzero_app_mode";

// Detect whether we are running inside the Android/TWA/installed-PWA app context.
// Sources (any of these -> app mode, persisted in localStorage so it survives
// login + navigation):
//   - URL flag ?source=twa  | ?source=pwa  | ?platform=android
//   - document.referrer starting with "android-app://" (the reliable TWA signal)
//   - a previously persisted flag in localStorage
// Escape hatch: ?source=web or ?platform=web clears the flag (back to normal web).
function detectAppMode() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = (params.get("source") || "").toLowerCase();
    const platform = (params.get("platform") || "").toLowerCase();
    const ref = (document.referrer || "").toLowerCase();

    // Explicit web override
    if (source === "web" || platform === "web") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }

    const appByUrl =
      source === "twa" ||
      source === "pwa" ||
      source === "android" ||
      platform === "android" ||
      platform === "twa";
    const appByReferrer = ref.startsWith("android-app://");

    if (appByUrl || appByReferrer) {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppModeProvider({ children }) {
  // Detect once on mount; it is stable for the session.
  const isAppMode = useMemo(() => detectAppMode(), []);
  const value = useMemo(() => ({ isAppMode }), [isAppMode]);
  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
}

export function useAppMode() {
  return useContext(AppModeContext);
}
